/** PATCH helper for the pre-trip item CATEGORY editor. Categories have no
 *  `updated_ts` — writes are last-writer-wins, so there is no optimistic-
 *  concurrency token and no 409 flow (like regions). `is_system` rows are
 *  read-only server-side (403); the UI hides save for them. */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ConsoleApiError } from '@/api/client';
import * as typesApi from '@/api/preTripItemTypes';
import type { PreTripItemTypeRow, PreTripItemTypeWriteInput } from '@/api/preTripItemTypes';
import { useSnackbar } from '@/components/Snackbar';

export function usePreTripItemTypePatch(type: PreTripItemTypeRow, onSaved: () => void) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (body: PreTripItemTypeWriteInput) => typesApi.update(type.id, body),
    onSuccess: () => {
      snackbar.show('Saved.', 'success');
      qc.invalidateQueries({ queryKey: ['pre_trip_item_types'] });
      onSaved();
    },
    onError: (e) => {
      const msg = e instanceof ConsoleApiError ? e.message : 'Save failed.';
      snackbar.show(msg, 'error');
    },
  });

  return {
    save: (body: PreTripItemTypeWriteInput) => mutation.mutate(body),
    saving: mutation.isPending,
    /** Field-keyed server errors from a 400, if any. */
    fieldErrors:
      mutation.error instanceof ConsoleApiError && mutation.error.status === 400
        ? (mutation.error.body as Record<string, unknown>)
        : null,
  };
}
